import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, CreditCard } from 'lucide-react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API, getHeaders, useBrand, stripePromise } from '../portalShared';
import BrandCollateral from '../BrandCollateral';
import { trackPaymentMethodAdded } from '../../../utils/analytics';

function CardSetupForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/portal/payment/setup`, {}, { headers: getHeaders() });
      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        setError(result.error.message);
      } else {
        await axios.post(`${API}/portal/payment/confirm`, { setup_intent_id: data.setup_intent_id }, { headers: getHeaders() });
        trackPaymentMethodAdded({ source: 'brand_portal' });
        onSuccess();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to set up payment');
    } finally { setProcessing(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white/5 border border-white/10 p-4 rounded">
        <CardElement options={{
          style: {
            base: { color: '#fff', fontSize: '14px', '::placeholder': { color: '#6B7280' } },
            invalid: { color: '#FF4343' },
          },
        }} />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <button type="submit" disabled={!stripe || processing} data-testid="save-card-btn"
        className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2.5 text-sm font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
        {processing ? 'Processing...' : 'Save Card'}
      </button>
    </form>
  );
}

function BrandPaymentSettings({ brand, onUpdate }) {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkPayment(); }, []);

  const checkPayment = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/payment/status`, { headers: getHeaders() });
      setPaymentStatus(data);
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <p className="text-[#9CA3AF] text-sm">Loading payment info...</p>;

  return (
    <div className="bg-[#111827] border border-white/10 p-5 sm:p-6" data-testid="payment-settings">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className="w-5 h-5 text-[#FF4343]" />
        <h3 className="font-semibold text-white">Payment Method</h3>
      </div>
      {paymentStatus?.has_payment_method ? (
        <div className="flex items-center gap-3 bg-[#10B981]/10 border border-[#10B981]/20 p-3 mb-4">
          <CheckCircle className="w-5 h-5 text-[#10B981]" />
          <div>
            <p className="text-sm text-white font-medium">{paymentStatus.card_brand?.toUpperCase()} •••• {paymentStatus.card_last4}</p>
            <p className="text-xs text-[#6B7280]">Card on file — ready to fund campaigns and search executives</p>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-xs text-[#9CA3AF] mb-4">Add a credit card to search executives, send offers, and fund campaigns.</p>
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <CardSetupForm onSuccess={() => { checkPayment(); if (onUpdate) onUpdate(); }} />
            </Elements>
          ) : (
            <p className="text-xs text-red-400">Stripe not configured.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function PortalSettings() {
  const brand = useBrand();
  const [form, setForm] = useState({ contact_name: brand?.contact_name || '', contact_email: brand?.contact_email || '', password: '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/portal/settings`, payload, { headers: getHeaders() });
    } catch {} finally { setSaving(false); }
  };
  return (
    <div data-testid="portal-settings" className="max-w-3xl space-y-6">
      <h3 className="text-sm font-semibold mb-4">Settings</h3>
      <BrandPaymentSettings brand={brand} />
      <BrandCollateral />
      <div className="bg-[#111827] border border-white/10 p-5 sm:p-6 space-y-4">
        <h4 className="font-semibold text-white text-sm">Account Details</h4>
        <input placeholder="Contact Name" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}
          className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        <input placeholder="Contact Email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})}
          className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        <input type="password" placeholder="New Password (leave blank to keep)" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
          className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        <button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

